import {Component, computed, inject, Inject, OnInit, signal, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Q9CreditBalanceWarningComponent} from '@q9elements/ui-kit/components';
import {ExternalGptConfig, GenerateStoriesAIData} from './models/generate-stories-dialog';
import {GenerateStoriesAIService} from './services/generate-stories-ai.service';
import {generateStoriesLink} from '../../../shared/constants/elements-links';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {RESOURCE_TYPES} from '../../../diagram-render/shape-spawn-data/resources.model';
import {PermissionsService, SessionService} from '@q9elements/ui-kit/common';
import {get} from 'lodash';

@Component({
  selector: 'app-generate-stories-ai-dialog',
  templateUrl: 'generate-stories-ai-dialog.component.html',
  styleUrls: ['generate-stories-ai-dialog.component.scss']
})
export class GenerateStoriesAiDialogComponent implements OnInit {
  @ViewChild('creditWarning') creditWarning!: Q9CreditBalanceWarningComponent;
  private readonly sessionService = inject(SessionService);
  private readonly permissionsService = inject(PermissionsService);

  public readonly isFree = this.permissionsService.isFree;
  public readonly hasSubscription = computed(() => {
    const user = this.sessionService.user();

    return !!get(user, 'userInTeam.team.subscription.subscriptionId');
  });

  public readonly articleLink = generateStoriesLink;
  public modalTitle: string;
  public step = signal(1);
  public plannedStoryCount = 0;
  public externalGptConfig$: Observable<ExternalGptConfig> = this.generateStoriesAIService
    .getExternalGptConfig()
    .pipe(
      tap(externalGptConfig => {
        this.modalTitle = !externalGptConfig?.enabled
          ? 'GPT_STORIES.GENERATE_STORIES_ELEMENTS_GPT'
          : 'GPT_STORIES.GENERATE_STORIES_EXTERNAL_GPT';
      })
    );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GenerateStoriesAIData,
    private generateStoriesAIService: GenerateStoriesAIService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    public dialogRef: MatDialogRef<GenerateStoriesAiDialogComponent>
  ) {}

  public ngOnInit(): void {
    const dv = this.diagramJsonSvc.get();

    this.plannedStoryCount = this.computePlannedStoryCount(dv, this.data?.activityMxKeys);
  }

  public changeStep(change: number) {
    this.step.update(value => value + change);
  }

  private computePlannedStoryCount(dv, activityMxKeys: number[]): number {
    if (!dv || !Array.isArray(dv.activities) || !Array.isArray(dv.flowlines)) {
      return 0;
    }

    let total = 0;

    for (const mxKey of activityMxKeys || []) {
      const activity = dv.activities.find(a => Number(a.mxKey) === Number(mxKey));

      if (!activity) {
        continue;
      }

      const inputs = dv.flowlines.filter(fl => Number(fl.tgtId) === Number(activity.mxKey));
      const outputs = dv.flowlines.filter(fl => Number(fl.srcId) === Number(activity.mxKey));

      if (inputs.length === 0 || outputs.length === 0) {
        continue;
      }

      const resources = Array.isArray(activity.resources) ? activity.resources : [];

      const humanResources = resources.filter(
        r => (r.type || '').toLowerCase() === RESOURCE_TYPES.Human
      );

      if (humanResources.length > 0) {
        total += humanResources.length;
        continue;
      }

      const systemResources = resources.filter(r => (r.type || '') !== RESOURCE_TYPES.Human);

      if (systemResources.length > 0) {
        total += systemResources.length;
        continue;
      }

      total += 1;
    }

    return total;
  }
}
